import { ComponentType, createElement, isValidElement, ReactNode, SVGProps } from "react";
import ReactMarkdown from "react-markdown";
import { Link as RouterLink } from "react-router-dom";
import { Box, Card, Link, Stack, styled, Typography as T } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import AccessLevelChip from "datasets/components/AccessLevelChip";
import { usePreviewMode } from "datasets/hooks/usePreviewMode";
import { DatasetListItem } from "datasets/types/interfaces/datasetListItem";
import { getAccentColor } from "datasets/utils/color";
import { format, formatRelative } from "date-fns";
import pluralize from "pluralize";
import prettyBytes from "pretty-bytes";
import stripMarkdown from "strip-markdown";
import { CardIllustration, ClockIcon, HardDrivesIcon, TableIcon } from "~/assets/icons";
import CT from "~/components/ClampedTypography";

const formatDate = (timestamp: string) => {
  const date = new Date(timestamp);
  return format(date, "MMM dd, yyyy");
};

const formatRelativeDate = (timestamp: string) => {
  const date = new Date(timestamp);
  const relativeFormat = formatRelative(date, new Date());
  return relativeFormat.split(" at ")[0];
};

interface BackgroundCardProps {
  accentColor: string;
}

const BackgroundCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "accentColor",
})<BackgroundCardProps>(({ accentColor }) => ({
  borderRadius: 8,
  display: "flex",
  flex: 1,
  overflow: "visible",
  padding: 0,
  paddingTop: 26,
  position: "relative",
  boxShadow: `
    0 1px 2px rgba(15, 23, 42, 0.03),
    0 0 0 1px rgba(15, 23, 42, 0.04)
  `,
  transition: "all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1)",

  "&:before": {
    borderRadius: 8,
    content: "''",
    position: "absolute",
    top: -1,
    left: 0,
    width: "100%",
    height: "40px",
    backgroundColor: accentColor,
  },

  "&:after": {
    borderRadius: "inherit",
    boxShadow: "0 4px 10px rgba(15, 23, 42, 0.06)",
    content: "''",
    height: "100%",
    inset: 0,
    opacity: 0,
    position: "absolute",
    transition: "all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1)",
    zIndex: -1,
  },

  "&:hover": {
    transform: "scale(1.02)",
  },

  "&:hover:after": {
    opacity: 1,
  },
}));

const ForegroundCard = styled(Card)({
  borderRadius: 8,
  borderTopLeftRadius: 0,
  boxShadow: "none",
  overflow: "visible",
  paddingBlock: 16,
  paddingInline: 24,
  position: "relative",
  width: "100%",
});

const Stat = ({
  children,
  icon,
}: {
  children: ReactNode;
  icon: ReactNode | ComponentType<SVGProps<SVGSVGElement>>;
}) => {
  // If icon is already a React element, use it directly. Otherwise, treat it as a component.
  // TODO: deprecate custom icons if possible and snap to an icon library like Lucide or MUI Icons
  const IconElement = isValidElement(icon)
    ? icon
    : createElement(icon as ComponentType<SVGProps<SVGSVGElement>>, {
        color: "inherit",
        width: 13,
        height: 13,
      });

  return (
    <Box color="grey.500" display="flex" alignItems="center" gap={0.5}>
      {IconElement}
      <T
        variant="body2"
        sx={{
          color: "grey.700",
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {children}
      </T>
    </Box>
  );
};

const DatasetCard = ({
  accent_color: accentColor,
  access_level,
  components,
  description,
  id,
  size = "medium",
  title,
  updated_at,
}: { size?: "small" | "medium" } & DatasetListItem) => {
  // Preview datasets omit `components`; show them as empty.
  const tableCount = components?.tables?.count ?? 0;
  const isPreviewModeEnabled = usePreviewMode();

  return (
    <Link
      to={`/data-browser/datasets/${id}`}
      data-testid="dataset-card"
      component={RouterLink}
      underline="none"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "1 1 auto",
      }}
    >
      <BackgroundCard
        accentColor={getAccentColor(accentColor)}
        sx={{ height: size === "medium" ? 210 : 220 }}
      >
        <ForegroundCard>
          <Box position="absolute" top={-16} left={0} right={0}>
            <CardIllustration color="white" width={95} height={29} />
          </Box>
          <Stack gap={1} height="100%">
            <Box display="grid" gap={1} gridTemplateColumns="1fr auto" width="100%">
              <CT component="h2" fontSize={16} lineHeight="19px" fontWeight={600}>
                {title}
              </CT>
              <AccessLevelChip
                accessLevel={access_level}
                isPreviewModeEnabled={isPreviewModeEnabled}
                showTooltip
                sx={{
                  color: darken(getAccentColor(accentColor), 0.2),
                  backgroundColor: lighten(getAccentColor(accentColor), 0.92),
                }}
              />
            </Box>
            <Box flex={1}>
              <CT component="div" sx={{ WebkitLineClamp: size === "medium" ? 4 : 2 }}>
                <Box
                  className="prose prose-sm max-w-none"
                  sx={{
                    pt: 0.5,
                    color: "grey.700",
                    fontSize: 12,
                    lineHeight: 1.3,
                    "& > *": { m: 0, p: 0 },
                    "&:empty": {
                      "&::before": {
                        content: '"No description"',
                        fontStyle: "italic",
                      },
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[stripMarkdown]}>{description}</ReactMarkdown>
                </Box>
              </CT>
              {size === "small" && updated_at && (
                <T
                  variant="body2"
                  sx={{
                    color: "grey.700",
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.3,
                    mt: 0.5,
                  }}
                >
                  Updated {formatRelativeDate(updated_at)}
                </T>
              )}
            </Box>
            <Box display="flex" alignItems="center" justifySelf="flex-end" gap={1} pb={0.5}>
              <Stat icon={TableIcon}>{`${tableCount} ${pluralize("Table", tableCount)}`}</Stat>

              <Stat icon={HardDrivesIcon}>{prettyBytes(components?.tables?.total_bytes ?? 0)}</Stat>

              {size === "medium" && updated_at && (
                <Stat icon={ClockIcon}>{formatDate(updated_at)}</Stat>
              )}
            </Box>
          </Stack>
        </ForegroundCard>
      </BackgroundCard>
    </Link>
  );
};

export default DatasetCard;
